import { AppInstanceAppearanceType, AppInstanceAssignmentType, MayBeNull } from '@wpp-open/core'
import { useEffect, useMemo } from 'react'

import { useAppInstanceApi } from 'api/apps/queries/useAppInstanceApi'
import { useNavigationTreeAncestorsApi } from 'api/navigation/queries/useNavigationTreeAncestorsApi'
import { is403Error, is404Error } from 'api/utils'
import { AppDisabledError, AppUnsupportedAppearanceError } from 'components/renderError'
import { useCurrentTenantData } from 'providers/currentTenantData/CurrentTenantDataContext'
import { OsStateError } from 'providers/osState/sideEffects/OsStateError'
import { isValueSet, UnsetValue } from 'providers/osState/utils/conditionalData'
import { useAppInstanceLoaded } from 'providers/osState/utils/resolvers/appInstance/useAppInstanceLoaded'
import { getInvalidResolvedAppData, getLoadingResolvedAppData } from 'providers/osState/utils/resolvers/common'
import { ResolvedAppData } from 'providers/osState/utils/useResolveAppData'
import { AppInstanceDataFromUrl } from 'types/osState/appDataFromUrl'
import {
  isAppearanceSupported,
  isAppInstanceDeleted,
  isAppInstanceStatusInactive,
  isValidAppearancesConfig,
  mapAppInstanceToApp,
} from 'utils/appInstances'

interface Params {
  appDataShort: AppInstanceDataFromUrl
}

export const useAppInstanceAppDataResolver = (params: MayBeNull<Params>): MayBeNull<ResolvedAppData> => {
  const isEnabled = !!params
  const appInstanceId = isEnabled ? params.appDataShort.appInstanceId : UnsetValue
  const { currentTenant, navigationTreeWithHiddenLevel, addNodesToTree } = useCurrentTenantData()

  // Load App Instance if enabled
  const {
    isLoading: isAppInstanceLoading,
    data: appInstance,
    error: appInstanceError,
  } = useAppInstanceApi({
    params: {
      id: isValueSet(appInstanceId) ? appInstanceId : '',
    },
    enabled: isEnabled,
  })

  const isProjectAppInstance = appInstance?.assignmentType === AppInstanceAssignmentType.Project
  const isWorkspaceAppInstance =
    appInstance?.assignmentType === AppInstanceAssignmentType.Workspace ||
    appInstance?.assignmentType === AppInstanceAssignmentType.Custom

  const isValidNavigationTreeForWorkspaceApp =
    isWorkspaceAppInstance && navigationTreeWithHiddenLevel.mapping[appInstance?.assignmentId]
  const isValidNavigationTreeForProjectApp =
    isProjectAppInstance &&
    (!appInstance?.context.workspaceId || navigationTreeWithHiddenLevel.mapping[appInstance?.context.workspaceId])
  const assignmentId = isProjectAppInstance ? appInstance?.context.workspaceId : appInstance?.assignmentId

  const {
    isLoading: isNavigationTreeLoading,
    data: navigationTreeSubLevels,
    error: navigationTreeError,
  } = useNavigationTreeAncestorsApi({
    params: {
      tenantId: currentTenant.id,
      parentId: assignmentId,
    },
    enabled: isEnabled && !!assignmentId,
  })

  useEffect(() => {
    if (navigationTreeSubLevels?.mapping && Object.keys(navigationTreeSubLevels?.mapping).length) {
      addNodesToTree({
        ...navigationTreeWithHiddenLevel,
        mapping: { ...navigationTreeSubLevels.mapping, ...navigationTreeWithHiddenLevel.mapping },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationTreeSubLevels])

  let result: MayBeNull<ResolvedAppData> = null
  // Process loading state and errors
  if (isEnabled) {
    if (isAppInstanceLoading || isNavigationTreeLoading) {
      result = getLoadingResolvedAppData()
    } else if (appInstanceError || navigationTreeError) {
      if (is404Error(appInstanceError)) {
        result = getInvalidResolvedAppData()
      } else {
        result = getLoadingResolvedAppData({
          sideEffectNode: <OsStateError isForbidden={is403Error(appInstanceError)} />,
        })
      }
    } else if (appInstance) {
      if (isAppInstanceDeleted(appInstance)) {
        // Deleted instances are ignored and considered the same as 404 response
        result = getInvalidResolvedAppData()
      } else if (isAppInstanceStatusInactive(appInstance) || !isValidAppearancesConfig(appInstance)) {
        // A separate error is shown for other inactive app statuses
        result = getLoadingResolvedAppData({
          sideEffectNode: <AppDisabledError />,
        })
      } else if (!isAppearanceSupported(AppInstanceAppearanceType.FULL_PAGE, appInstance)) {
        result = getLoadingResolvedAppData({
          sideEffectNode: <AppUnsupportedAppearanceError />,
        })
      }
    }
  }

  // App Instance is loaded and ready to be processed
  const isAppInstanceLoaded =
    isEnabled && !result && appInstance && (isValidNavigationTreeForWorkspaceApp || isValidNavigationTreeForProjectApp)
  const app = useMemo(
    () => (isAppInstanceLoaded ? mapAppInstanceToApp(appInstance) : UnsetValue),
    [appInstance, isAppInstanceLoaded],
  )

  // Processing part is split in a separate hook to simplify the conditional logic
  const appInstanceLoadedResult = useAppInstanceLoaded(
    isAppInstanceLoaded && isValueSet(app)
      ? {
          appInstance,
          app,
          appDataShort: params.appDataShort,
        }
      : null,
  )

  if (appInstanceLoadedResult) {
    result = appInstanceLoadedResult
  }

  return isEnabled ? result || getInvalidResolvedAppData() : null
}
